import { Link } from "react-router-dom";
import { Heart, Compass, Waves } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Our Story — Ymaku's personal journey.
 * Editorial body copy. `[Ymaku's name]` is left as a placeholder for her
 * to replace with her preferred name when she reviews.
 */
export default function OurStory() {
    const { t } = useLang();
    return (
        <div className="bg-cream" data-testid="story-page">
            {/* Editorial hero */}
            <section className="relative pt-24 md:pt-32 pb-10 md:pb-14 bg-cream-warm overflow-hidden">
                <div className="max-w-4xl mx-auto px-5 md:px-8">
                    <span className="divider-rule text-brown" data-testid="story-eyebrow">
                        <Heart size={12} /> {t("story.eyebrow")}
                    </span>
                    <h1
                        className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5rem] leading-[0.98] text-forest mt-6"
                        data-testid="story-headline"
                    >
                        {t("story.h1")}
                    </h1>
                    <p className="mt-10 font-serif text-2xl md:text-3xl text-gold italic leading-[1.3]">
                        {t("story.lede1")}
                    </p>
                    <p className="mt-4 text-brown text-lg md:text-xl leading-relaxed">
                        {t("story.lede2")}
                    </p>
                </div>
            </section>

            {/* Editorial body */}
            <section className="pt-6 pb-20 md:pb-28" data-testid="story-body">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <div className="prose-island space-y-7 text-brown text-lg leading-[1.75]">
                        <p>
                            <span data-testid="story-maker-placeholder-1" className="font-serif italic text-gold">
                                We
                            </span>{" "}
                            {t("story.body1_suffix")}
                        </p>

                        <p>{t("story.body2")}</p>

                        <p>
                            {t("story.body3_prefix")}{" "}
                            <em className="text-forest font-serif">{t("story.body3_love")}</em>{" "}
                            {t("story.body3_suffix")}
                        </p>

                        {/* Pull-quote */}
                        <blockquote
                            className="my-12 border-l-2 border-gold pl-6 md:pl-8 py-2"
                            data-testid="story-pullquote"
                        >
                            <p className="font-serif italic text-2xl md:text-3xl text-forest leading-[1.3]">
                                {t("story.pullquote")}
                            </p>
                        </blockquote>

                        <p>
                            <span data-testid="story-maker-placeholder-2" className="font-serif italic text-gold">
                                We
                            </span>{" "}
                            {t("story.body4_suffix")}
                        </p>

                        <p>{t("story.body5")}</p>

                        <p className="font-serif text-xl text-forest italic">
                            {t("story.body6")}
                        </p>

                        <p className="pt-4">
                            <span className="block text-gold font-serif text-2xl mb-1">
                                {t("story.close.l1")}
                            </span>
                            <span className="block text-gold font-serif text-2xl mb-1">
                                {t("story.close.l2")}
                            </span>
                            <span className="block text-gold font-serif text-2xl">
                                {t("story.close.l3")}
                            </span>
                        </p>
                    </div>

                    {/* CTA */}
                    <div className="mt-16 flex flex-wrap items-center gap-4">
                        <Link
                            to="/inventory"
                            className="btn-pill btn-primary"
                            data-testid="story-first-drop-cta"
                        >
                            {t("story.cta.first_drop")}
                        </Link>
                        <Link
                            to="/about"
                            className="btn-pill btn-ghost"
                            data-testid="story-craft-cta"
                        >
                            {t("story.cta.craft")}
                        </Link>
                    </div>
                </div>
            </section>

            {/* Three islands band — context without competing with the editorial body */}
            <section className="py-20 md:py-24 bg-cream-warm" data-testid="story-islands">
                <div className="max-w-6xl mx-auto px-5 md:px-8">
                    <span className="divider-rule text-brown">
                        <Compass size={12} /> Three Islands · One Line
                    </span>
                    <h2 className="font-serif text-3xl md:text-5xl text-forest mt-5 max-w-3xl leading-[1.05]">
                        Every bar carries all three homes.
                    </h2>
                    <div className="mt-14 grid md:grid-cols-3 gap-10">
                        {[
                            {
                                h: "Chuuk",
                                p: "The atolls where the lineage begins. Sea salt, coconut, the smell of saltwater on wood. The quiet.",
                            },
                            {
                                h: "Guam",
                                p: "Markets, aunties, jungle. Where the recipes were learned — soap, infusions, medicine from plants.",
                            },
                            {
                                h: "Hawaiʻi",
                                p: "Volcanic soil, turmeric, kukui, hibiscus. Where the studio sits today. Where the hands landed.",
                            },
                        ].map((v, i) => (
                            <article
                                key={v.h}
                                className="border-t border-forest/15 pt-6 rise-in"
                                style={{ animationDelay: `${i * 120}ms` }}
                                data-testid={`story-island-${v.h.toLowerCase().replace(/[^a-z]/g, "")}`}
                            >
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                    0{i + 1}
                                </p>
                                <h3 className="font-serif text-3xl text-forest mt-3">
                                    {v.h}
                                </h3>
                                <p className="mt-4 text-brown leading-relaxed">
                                    {v.p}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Closing wave */}
            <section className="py-16 md:py-20" data-testid="story-close">
                <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
                    <Waves size={20} className="mx-auto text-gold" aria-hidden />
                    <p className="mt-5 font-serif text-xl md:text-2xl text-forest italic">
                        {t("story.mahalo")}
                    </p>
                </div>
            </section>
        </div>
    );
}
